import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, statusClass } from "../lib/utils";
import { Plus, PencilSimple, Trash, Receipt, CurrencyEur, Users, Buildings, User, Storefront, MagnifyingGlass, X as XIcon, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import DangerConfirm from "../components/DangerConfirm";

export default function AdminClients() {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);
  const [search, setSearch] = useState("");
  const [askDelete, setAskDelete] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/clients");
    setClients(data);
  };

  useEffect(() => { load(); }, []);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/pricelists/backfill-defaults");
      if (data.fixed.length === 0) {
        toast.success(`Tutti i ${data.checked} clienti hanno già un listino completo`);
      } else {
        toast.success(`Listino completato per ${data.fixed.length} client${data.fixed.length === 1 ? "e" : "i"} su ${data.checked} controllati`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la verifica listini");
    } finally {
      setBackfilling(false);
    }
  };

  const doRemove = async (id) => {
    await api.delete(`/admin/clients/${id}`);
    toast.success("Cliente eliminato");
    load();
  };
  const remove = (c) => setAskDelete(c);

  const filtered = search.trim()
    ? clients.filter((c) => {
        const q = search.toLowerCase().trim();
        return (
          (c.company_name || "").toLowerCase().includes(q) ||
          (c.contact_email || "").toLowerCase().includes(q) ||
          (c.contact_name || "").toLowerCase().includes(q) ||
          (c.vat_number || "").toLowerCase().includes(q) ||
          (c.codice_fiscale || "").toLowerCase().includes(q) ||
          (c.myze_shop_id || "").toLowerCase().includes(q) ||
          (c.myze_company_id || "").toLowerCase().includes(q)
        );
      })
    : clients;

  return (
    <div className="space-y-6" data-testid="admin-clients">
      <PageHeader
        eyebrow="Clienti"
        title="I tuoi clienti"
        subtitle="Wallet, listini, credenziali e stato di ogni brand collegato a Teefarm."
        chip="tf-chip-lavender"
        icon={Users}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={runBackfill} disabled={backfilling} className="tf-btn tf-btn-ghost" data-testid="backfill-pricelists-btn">
              <ArrowsClockwise size={16} weight="bold" /> {backfilling ? "Verifica in corso…" : "Verifica listini"}
            </button>
            <button onClick={() => setCreating(true)} className="tf-btn tf-btn-primary" data-testid="new-client-btn">
              <Plus size={16} weight="bold" /> {t("new_client")}
            </button>
          </div>
        }
      />

      {/* Search bar */}
      <div className="tf-card p-3 md:p-4 flex items-center gap-3" data-testid="clients-search-bar">
        <div className="w-10 h-10 rounded-2xl bg-[var(--tf-bg)] flex items-center justify-center flex-shrink-0">
          <MagnifyingGlass size={16} weight="bold" className="text-[var(--tf-muted)]" />
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, email, P.IVA, C.F., ID Myze…"
          className="flex-1 bg-transparent border-none outline-none text-sm font-display font-bold text-[var(--tf-ink)] placeholder:text-[var(--tf-muted)] placeholder:font-normal min-w-0"
          data-testid="clients-search-input"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-[var(--tf-muted)] hover:text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition flex-shrink-0"
            data-testid="clients-search-clear"
            aria-label="Cancella ricerca"
          >
            <XIcon size={14} weight="bold" />
          </button>
        )}
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--tf-muted)] tf-numeric flex-shrink-0">
          {filtered.length}/{clients.length}
        </span>
      </div>

      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead>
              <tr>
                <th>{t("company")}</th><th>{t("contact")}</th><th>Myze</th>
                <th>{t("status")}</th><th className="text-right">{t("balance")}</th>
                <th className="text-right">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--tf-muted)] py-10">{t("empty")}</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td>
                    <Link to={`/admin/clients/${c.id}`} className="hover:text-[var(--tf-ink)] hover:underline block" data-testid={`open-row-${c.id}`}>
                      <div className="font-medium">{c.company_name}</div>
                      <div className="text-xs text-[var(--tf-muted)] font-mono">{c.vat_number || "—"}</div>
                    </Link>
                  </td>
                  <td>
                    <div>{c.contact_name}</div>
                    <div className="text-xs text-[var(--tf-muted)]">{c.contact_email}</div>
                  </td>
                  <td className="font-mono text-xs">
                    <div>{c.myze_company_id || "—"}</div>
                    <div className="text-[var(--tf-muted)]">{c.myze_shop_id || "—"}</div>
                    <span
                      className={`inline-block mt-1 tf-status-pill text-[9px] font-sans ${c.myze_shop_id ? "tf-status-success" : "tf-status-error"}`}
                      data-testid={`shop-badge-${c.id}`}
                    >
                      {c.myze_shop_id ? "Store collegato" : "Store mancante"}
                    </span>
                  </td>
                  <td><span className={`tf-status-pill ${statusClass(c.status)}`}>{c.status}</span></td>
                  <td className="text-right tf-numeric font-medium">{fmtEur(c.wallet_balance)}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/admin/pricelists?client=${c.id}`} className="tf-btn tf-btn-ghost !px-2 !py-1.5" title={t("pricelists")} data-testid={`pl-${c.id}`}>
                        <Receipt size={14} weight="bold" />
                      </Link>
                      <button onClick={() => setAdjustFor(c)} className="tf-btn tf-btn-ghost !px-2 !py-1.5" title="Wallet adj" data-testid={`adj-${c.id}`}>
                        <CurrencyEur size={14} weight="bold" />
                      </button>
                      <button onClick={() => setEditing(c)} className="tf-btn tf-btn-ghost !px-2 !py-1.5" data-testid={`edit-${c.id}`}>
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => remove(c)} className="tf-btn tf-btn-danger !px-2 !py-1.5" data-testid={`del-${c.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="admin-clients-mlist">
        {filtered.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm">
            <Users size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {search ? "Nessun cliente corrisponde alla ricerca" : t("empty")}
          </div>
        )}
        {filtered.map((c) => {
          const isPrivate = c.entity_type === "private";
          return (
            <div key={c.id} className="tf-mrow flex-col items-stretch" data-testid={`client-card-${c.id}`}>
              <Link
                to={`/admin/clients/${c.id}`}
                className="flex items-center gap-3 hover:opacity-90 transition"
                data-testid={`open-client-${c.id}`}
              >
                <div
                  className="tf-mrow-icon"
                  style={{
                    background: isPrivate ? "#E7E1FA" : "#DAECFA",
                    color: isPrivate ? "#4B33A0" : "#1F5A93",
                  }}
                >
                  {isPrivate ? <User size={20} weight="fill" /> : <Buildings size={20} weight="fill" />}
                </div>
                <div className="tf-mrow-body">
                  <div className="tf-mrow-title truncate">{c.company_name}</div>
                  <div className="tf-mrow-sub flex items-center gap-1.5 truncate">
                    <span className="truncate">{c.contact_email || "—"}</span>
                  </div>
                  {(c.myze_shop_id || c.myze_company_id) && (
                    <div className="tf-mrow-sub flex items-center gap-1.5 mt-0.5 truncate">
                      <Storefront size={11} weight="bold" />
                      <span className="font-mono truncate">{c.myze_shop_id || c.myze_company_id}</span>
                    </div>
                  )}
                  <span
                    className={`inline-block mt-1 tf-status-pill text-[9px] ${c.myze_shop_id ? "tf-status-success" : "tf-status-error"}`}
                    data-testid={`shop-badge-card-${c.id}`}
                  >
                    {c.myze_shop_id ? "Store collegato" : "Store mancante"}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="font-display font-black text-sm tf-numeric text-[var(--tf-ink)]">{fmtEur(c.wallet_balance)}</div>
                  <span className={`tf-status-pill ${statusClass(c.status)} text-[9px]`}>{c.status}</span>
                </div>
              </Link>
              <div className="flex items-center gap-2 pt-3 mt-3 border-t border-[var(--tf-border)]">
                <Link
                  to={`/admin/pricelists?client=${c.id}`}
                  className="flex-1 rounded-2xl border border-[var(--tf-border)] py-2.5 flex items-center justify-center gap-1.5 text-xs font-semibold text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition"
                  data-testid={`pl-card-${c.id}`}
                >
                  <Receipt size={13} weight="bold" /> Listino
                </Link>
                <button
                  onClick={() => setAdjustFor(c)}
                  className="flex-1 rounded-2xl border border-[var(--tf-border)] py-2.5 flex items-center justify-center gap-1.5 text-xs font-semibold text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition"
                  data-testid={`adj-card-${c.id}`}
                >
                  <CurrencyEur size={13} weight="bold" /> Wallet
                </button>
                <button
                  onClick={() => setEditing(c)}
                  className="w-10 h-10 rounded-2xl border border-[var(--tf-border)] flex items-center justify-center text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition flex-shrink-0"
                  data-testid={`edit-card-${c.id}`}
                  aria-label="Modifica"
                >
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button
                  onClick={() => remove(c)}
                  className="w-10 h-10 rounded-2xl border border-[var(--tf-border)] flex items-center justify-center text-[var(--tf-muted)] hover:text-[var(--tf-error)] hover:bg-[#FBDDE1] transition flex-shrink-0"
                  data-testid={`del-card-${c.id}`}
                  aria-label="Elimina"
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {(creating || editing) && (
        <ClientModal
          client={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { load(); setCreating(false); setEditing(null); }}
        />
      )}
      {adjustFor && <AdjustModal client={adjustFor} onClose={() => setAdjustFor(null)} onDone={() => { setAdjustFor(null); load(); }} />}

      <DangerConfirm
        open={!!askDelete}
        onClose={() => setAskDelete(null)}
        onConfirm={async () => { await doRemove(askDelete.id); setAskDelete(null); }}
        title="Eliminare questo cliente?"
        subtitle={askDelete?.company_name}
        description="Il cliente verrà rimosso definitivamente insieme al suo wallet, ordini e transazioni. L'azione è irreversibile."
        confirmLabel="Elimina cliente"
        verifyWord="ELIMINA"
        testId="delete-client-confirm"
      />
    </div>
  );
}

function ClientModal({ client, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(client || {
    company_name: "", contact_email: "", contact_name: "",
    vat_number: "", myze_company_id: "", myze_shop_id: "",
    notes: "", create_user: true, user_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (client) {
        await api.patch(`/admin/clients/${client.id}`, form);
        toast.success(t("saved"));
        onSaved();
      } else {
        const { data } = await api.post("/admin/clients", form);
        if (data.generated_password) setResult(data);
        else { toast.success("Cliente creato"); onSaved(); }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <Modal onClose={() => { onSaved(); }}>
        <div className="p-6">
          <h3 className="font-display font-bold text-2xl">Cliente creato</h3>
          <p className="text-sm text-[var(--tf-muted)] mt-2 mb-6">Credenziali iniziali (mostrate solo ora):</p>
          <div className="tf-card p-4 space-y-2 bg-[var(--tf-bg)] font-mono text-sm">
            <div><span className="tf-label">Email</span><div>{result.contact_email}</div></div>
            <div><span className="tf-label">Password</span><div>{result.generated_password}</div></div>
          </div>
          <button onClick={() => onSaved()} className="tf-btn tf-btn-primary mt-6 w-full">OK</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <form onSubmit={save} className="p-6 space-y-4" data-testid="client-modal">
        <h3 className="font-display font-bold text-2xl">{client ? t("edit") : t("new_client")}</h3>
        <Field label={t("company_name")} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} required testid="field-company" />
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("contact_name")} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required testid="field-contact-name" />
          <Field label={t("contact_email")} type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} required testid="field-contact-email" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label={t("vat_number")} value={form.vat_number || ""} onChange={(v) => setForm({ ...form, vat_number: v })} testid="field-vat" />
          <Field label={t("myze_company_id")} value={form.myze_company_id || ""} onChange={(v) => setForm({ ...form, myze_company_id: v })} testid="field-myze-co" />
          <Field label={t("myze_shop_id")} value={form.myze_shop_id || ""} onChange={(v) => setForm({ ...form, myze_shop_id: v })} testid="field-myze-shop" />
        </div>
        <Field label={t("notes")} value={form.notes || ""} onChange={(v) => setForm({ ...form, notes: v })} testid="field-notes" />
        {!client && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.create_user} onChange={(e) => setForm({ ...form, create_user: e.target.checked })} data-testid="field-create-user" />
              {t("create_user")}
            </label>
            {form.create_user && (
              <Field label={t("user_password") + " (optional - auto if empty)"} value={form.user_password || ""} onChange={(v) => setForm({ ...form, user_password: v })} testid="field-user-pw" />
            )}
          </>
        )}
        {client && (
          <div>
            <label className="tf-label block mb-2">{t("status")}</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="tf-input" data-testid="field-status">
              <option value="active">active</option><option value="suspended">suspended</option>
            </select>
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="tf-btn tf-btn-ghost flex-1">{t("cancel")}</button>
          <button type="submit" disabled={loading} className="tf-btn tf-btn-primary flex-1" data-testid="client-save-btn">{loading ? "..." : t("save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function AdjustModal({ client, onClose, onDone }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/wallet/${client.id}/adjust`, { amount: parseFloat(amount), note });
      toast.success("Wallet aggiornato");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4" data-testid="adjust-modal">
        <h3 className="font-display font-bold text-2xl">Rettifica wallet</h3>
        <p className="text-sm text-[var(--tf-muted)]">{client.company_name} · Saldo attuale: <span className="font-mono font-bold">{fmtEur(client.wallet_balance)}</span></p>
        <Field label={`${t("amount")} (positivo=accredito, negativo=storno)`} type="number" step="0.01" value={amount} onChange={setAmount} required testid="adj-amount" />
        <Field label={t("manual_note")} value={note} onChange={setNote} testid="adj-note" />
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="tf-btn tf-btn-ghost flex-1">{t("cancel")}</button>
          <button type="submit" disabled={loading} className="tf-btn tf-btn-primary flex-1" data-testid="adj-save-btn">{t("save")}</button>
        </div>
      </form>
    </Modal>
  );
}

export function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="tf-card bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto scroll-thin" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, required, step, testid }) {
  return (
    <div>
      <label className="tf-label block mb-2">{label}</label>
      <input type={type} step={step} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="tf-input" data-testid={testid} />
    </div>
  );
}
